import React, {useEffect, useState} from 'react'

// Third party libraries
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'

// Custom Components
import CountrySelectorModalContent from '../country-selector-modal-content'
import {useToast} from '../../hooks/use-toast'

// Custom Hooks
import useMultiSite from '../../hooks/use-multi-site'
import {useApplyShipToCountry} from '@app/hooks/use-apply-ship-to-country'

// Utils
import {sortShipToCountries} from '@app/utils/ship-to-country-utils'

import {CART_HREF} from '../../constants'

/**
 * CountrySelectorModalCheckout is a React functional component that provides a modal interface
 * for selecting a shipping country during the checkout process. It manages the state and behavior
 * related to country selection and updates the shopper's context accordingly.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isOpen - A boolean indicating whether the modal is open.
 * @param {function} props.onClose - A callback function to be invoked when the modal is closed.
 *
 * @returns {JSX.Element} A JSX element representing the country selector modal.
 */
const CountrySelectorModalCheckout = ({isOpen, onClose}) => {
    const [selectedCountryCode, setSelectedCountryCode] = useState()
    const [isLoading, setIsLoading] = useState(false)
    const [showWarning, setShowWarning] = useState(false)
    const [warningType, setWarningType] = useState(null)

    const toast = useToast()
    const intl = useIntl()
    const history = useHistory()
    const {buildUrl} = useMultiSite()

    const {applyShipToCountry, getCountryChangeTarget, shopperContext, shipToCountry} =
        useApplyShipToCountry()

    /**
     * Handles the change event for the country selection dropdown.
     *
     * @param {Object} event - The event object associated with the change event.
     * @param {Object} event.target - The target element of the event.
     * @param {string} event.target.value - The value of the selected country code.
     */
    const handleCountryChange = (event) => {
        const countryCode = event.target.value
        setSelectedCountryCode(countryCode)

        const currentCountryCode =
            shopperContext?.geoLocation?.countryCode ||
            shopperContext?.geoLocation?.country ||
            shipToCountry?.id

        if (countryCode === currentCountryCode) {
            setShowWarning(false)
            setWarningType(null)
            return
        }

        const target = getCountryChangeTarget(countryCode)

        setShowWarning(true)
        setWarningType(target.type === 'sameSite' ? 'sameSiteCountrySwitch' : 'siteSwitch')
    }

    /**
     * Handles the confirmation action when a user selects a country in the checkout modal.
     *
     * @returns {Promise<void>} Resolves when the country change process is complete.
     */
    const handleConfirm = async () => {
        if (!selectedCountryCode) return

        setIsLoading(true)

        try {
            const result = await applyShipToCountry(selectedCountryCode, {
                accepted: true
            })

            if (result.href) {
                onClose()
                window.location.href = result.href
                return
            }

            toast({
                title: 'Country updated successfully.',
                description: '',
                status: 'success'
            })

            onClose()
            history.push(buildUrl(CART_HREF))
        } catch (error) {
            toast({
                title: 'An error occurred.',
                description: error.message,
                status: 'error'
            })
        } finally {
            setIsLoading(false)
        }
    }

    /**
     * Handles the closure of the country selector modal.
     *
     * @returns {Promise<void>} Resolves after closing the modal.
     */
    const handleClose = async () => {
        onClose()
    }

    const countries = sortShipToCountries(intl)

    useEffect(() => {
        if (shipToCountry?.id && !selectedCountryCode) {
            setSelectedCountryCode(shipToCountry.id)
        }
    }, [selectedCountryCode, shipToCountry?.id])

    return (
        <CountrySelectorModalContent
            isOpen={isOpen}
            onClose={handleClose}
            geolocCountryCode={shipToCountry?.id}
            selectedCountryCode={selectedCountryCode}
            countries={countries}
            handleCountryChange={handleCountryChange}
            handleConfirm={handleConfirm}
            isLoading={isLoading}
            isCheckout={true}
            showWarning={showWarning}
            warningType={warningType}
            isConfirmDisabled={!selectedCountryCode}
        />
    )
}

CountrySelectorModalCheckout.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
}

export default CountrySelectorModalCheckout
